import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import Confetti from "@/components/Confetti";
import { billingApi } from "@/lib/api";
import { Check, Loader2, ArrowRight, AlertTriangle } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: "pending" });
  const [confetti, setConfetti] = useState(false);
  const triedRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "missing" });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      triedRef.current += 1;
      try {
        const r = await billingApi.status(sessionId);
        if (cancelled) return;
        setState(r);
        if (r.payment_status === "paid") {
          setConfetti(true);
          setTimeout(() => setConfetti(false), 1800);
          return;
        }
        if (r.status === "expired" || triedRef.current >= 8) return;
      } catch (e) { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const expired = state.status === "expired";

  return (
    <div className="min-h-screen relative overflow-hidden">
      {confetti && <Confetti />}
      <div className="blob" style={{ width: 480, height: 480, background: "#15FF73", top: -120, right: -100 }} />
      <NavBar />
      <div className="max-w-2xl mx-auto px-5 lg:px-10 py-20 relative">
        {paid ? (
          <div className="card-x card-green bg-[#15FF73] p-10 text-center">
            <Check size={42} strokeWidth={3} className="mx-auto mb-3" />
            <h1 className="font-display text-5xl font-extrabold tracking-tight mb-3">You're in.</h1>
            <p className="text-base mb-2">Payment confirmed · ${state.amount?.toFixed(2)}</p>
            <p className="text-sm font-mono-x font-bold uppercase tracking-widest opacity-70 mb-8">{state.package_id}</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to="/dashboard" className="btn-x btn-primary">Open dashboard <ArrowRight size={14} strokeWidth={2.4} /></Link>
              <Link to="/wallet" className="btn-x btn-ghost">Wallet →</Link>
            </div>
          </div>
        ) : expired ? (
          <div className="card-x card-magenta bg-[#FAF6E8] p-10 text-center">
            <AlertTriangle size={36} className="mx-auto mb-3 text-[#FF1A8C]" />
            <h1 className="font-display text-4xl font-extrabold mb-2">Session expired</h1>
            <p className="text-sm opacity-70 mb-6">Try again from the billing page.</p>
            <Link to="/billing" className="btn-x btn-primary">Back to billing</Link>
          </div>
        ) : (
          <div className="card-x card-cyan bg-[#FAF6E8] p-10 text-center">
            <Loader2 size={36} className="mx-auto mb-3 animate-spin" />
            <h1 className="font-display text-4xl font-extrabold mb-2">Confirming payment…</h1>
            <p className="text-sm opacity-70">Hang tight, this only takes a sec.</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
